import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Bell, Headset, Loader2, X } from "lucide-react";
import { isApiEnabled } from "../../api/config";
import {
  supportEscalationsService,
  type SupportEscalationSummary,
} from "../../services/supportEscalationsService";

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * Header bell: open support escalations across all exhibitions.
 * Red badge = count of open requests.
 */
export function AdminNotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<SupportEscalationSummary[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!isApiEnabled()) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const list = await supportEscalationsService.listGlobal("open");
      setItems(list);
    } catch {
      /* keep previous */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => {
      void load();
    }, 20_000);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    void load();
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, load]);

  const count = items.length;
  const badge = count > 9 ? "9+" : String(count);

  const openItem = (item: SupportEscalationSummary) => {
    if (!item.eventId) return;
    setOpen(false);
    navigate(
      `/support?event=${encodeURIComponent(item.eventId)}&session=${encodeURIComponent(item.sessionId)}`,
    );
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-500"
        aria-label={
          count > 0
            ? `Уведомления: ${count} открытых обращений`
            : "Уведомления"
        }
        aria-expanded={open}
        title="Обращения в поддержку"
      >
        <Bell size={16} />
        {count > 0 ? (
          <span className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-semibold leading-[14px] text-center">
            {badge}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-full mt-1.5 w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border border-gray-200 bg-white shadow-lg z-50 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
            <Headset size={14} className="text-sky-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-gray-900">Обращения</p>
              <p className="text-[11px] text-gray-400 truncate">
                По всем выставкам
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded-md text-gray-400 hover:bg-gray-50 hover:text-gray-600"
              aria-label="Закрыть"
            >
              <X size={14} />
            </button>
          </div>

          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-6 text-gray-400 text-[12px]">
              <Loader2 size={14} className="animate-spin" /> Загрузка…
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-4 text-[12px] text-gray-500">
              Нет открытых обращений
            </p>
          ) : (
            <ul className="max-h-[min(20rem,50vh)] overflow-y-auto divide-y divide-gray-50">
              {items.map((item) => (
                <li key={`${item.eventId}-${item.sessionId}`}>
                  <button
                    type="button"
                    onClick={() => openItem(item)}
                    className="w-full text-left px-3 py-2.5 hover:bg-sky-50/80 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        {item.eventTitle ? (
                          <p className="text-[10px] text-violet-700 truncate mb-0.5">
                            {item.eventTitle}
                            {item.eventCity ? ` · ${item.eventCity}` : ""}
                          </p>
                        ) : null}
                        <p className="text-[13px] text-gray-900 truncate font-medium">
                          {item.userName || "Посетитель"}
                        </p>
                        <p className="text-[12px] text-gray-600 truncate">
                          {item.pendingRequest || "Обращение в поддержку"}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                          {[item.phone, formatWhen(item.escalatedAt || item.updatedAt)]
                            .filter(Boolean)
                            .join(" · ")}
                          {item.claimedByName
                            ? ` · ${item.claimedByName}`
                            : " · свободен"}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-gray-100 px-3 py-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/support");
              }}
              className="w-full text-center text-[12px] text-indigo-600 hover:text-indigo-800 py-1"
            >
              Открыть чат поддержки
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
